export type ProjectStatus = "active" | "suspended" | "inactive";

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active: "Aktywne",
  suspended: "Wstrzymane",
  inactive: "Nieaktywne",
};

export const PROJECT_STATUS_COLORS: Record<ProjectStatus, "green" | "yellow" | "gray"> = {
  active: "green",
  suspended: "yellow",
  inactive: "gray",
};

/** Los umowy przedsięwzięcia — pisany wyłącznie z dokumentów umowy
 *  (wypowiedzenie → „w trakcie wypowiedzenia", porozumienie z datą → „wypowiedziana"). */
export type ProjectContractStatus = "active" | "terminating" | "terminated";

export const CONTRACT_STATUS_LABELS: Record<ProjectContractStatus, string> = {
  active: "Obowiązuje",
  terminating: "W trakcie wypowiedzenia",
  terminated: "Wypowiedziana",
};

export type ProjectContractDocumentKind =
  | "annex_term"
  | "annex_amount"
  | "annex_other"
  | "termination"
  | "settlement";

export const CONTRACT_DOCUMENT_KIND_LABELS: Record<ProjectContractDocumentKind, string> = {
  annex_term: "Aneks terminowy",
  annex_amount: "Aneks kwotowy",
  annex_other: "Aneks inny",
  termination: "Wypowiedzenie",
  settlement: "Porozumienie",
};

/** Wpis z GET /projects/{id}/contract-documents/. Plik pobiera się osobnym
 *  GET …/contract-documents/{id}/download/ (link podpisywany na klik). */
export interface ProjectContractDocument {
  id: string;
  kind: ProjectContractDocumentKind;
  kind_display: string;
  filename: string | null;
  /** Id skanu (ManagedFile). */
  file_id: string | null;
  description: string;
  new_end_date: string | null;
  previous_end_date: string | null;
  new_total_value: string | null;
  previous_total_value: string | null;
  marks_terminated: boolean;
  effective_date: string | null;
  note: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  /** Wycofany (nieaktualny) wpis — zostaje w historii z tym, kto, kiedy i dlaczego. */
  is_withdrawn: boolean;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  withdrawn_by_name: string | null;
  withdrawal_reason: string;
  /** Czy przy wycofaniu przywrócono termin/wartość/status sprzed dokumentu. */
  withdrawal_effect_reverted: boolean;
}

export interface Project {
  id: string;
  name: string;
  short_name: string;
  client: string | null;
  client_detail: { id: string; name: string; short_name: string; nip: string } | null;
  client_name: string;
  description: string;
  is_active: boolean;
  status: ProjectStatus;
  status_display: string;
  /** Czym jest to przedsięwzięcie: dokumentacją czy robotami. Projekt i budowa z jednego
   *  przetargu to DWA przedsięwzięcia, wiązane wspólną grupą. */
  work_scope: "projekt" | "budowa";
  work_scope_display: string;
  suspension_fraction: string;
  total_value: string | null;
  planned_end_date: string | null;
  planned_end_date_ceo: string | null;
  planned_end_date_director: string | null;
  coordinators: string[];
  coordinators_detail: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    /** Od kiedy koordynator (ISO); null gdy brak wpisu w rejestrze. */
    since: string | null;
    /** false = przypisany już przy starcie rejestru — `since` to start rejestru, nie realna data. */
    since_exact: boolean | null;
  }>;
  user_permissions: Array<{ id: string; user: string; user_name: string; user_email: string; can_view_amounts: boolean }>;
  linked_tender_id: string | null;
  /** Umowa z przetargu jest do pobrania — tylko dla osób z prawem podglądu przetargów. */
  has_contract_file?: boolean;
  /** Przedsięwzięcie ma podpięty projekt ZE (DLC_01) — jest dokąd przejść.
   *  Trasa workspace'u ZE kluczuje po id przedsięwzięcia, więc id profilu ZE zbędne. */
  has_ze_project?: boolean;
  /** Los umowy (aneksy nie zmieniają go; wypowiedzenie i porozumienie tak). */
  contract_status: ProjectContractStatus;
  contract_status_display: string;
  /** Data zawarcia porozumienia / uskutecznienia wypowiedzenia — tylko przy „terminated". */
  contract_terminated_on: string | null;
  company: string | null;
  company_name: string | null;
  group: string | null;
  group_detail: { id: string; name: string; short_name: string } | null;
  has_finance_access: boolean;
  client_branch: string | null;
  client_branch_detail: { id: string; name: string; city: string; display_name: string } | null;
  client_sub_branch: string | null;
  client_sub_branch_detail: { id: string; name: string; city: string; display_name: string } | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectGroup {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
  billed_together: boolean;
  total_value: string;
  planned_end_date: string | null;
  planned_end_date_ceo: string | null;
  planned_end_date_director: string | null;
  project_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectGroupFormData {
  name: string;
  short_name?: string;
  is_active?: boolean;
  billed_together?: boolean;
}

export interface FinancialSummary {
  actual_revenue: string;
  paid_revenue: string;
  planned_revenue: string;
  actual_contractor_cost: string;
  paid_contractor_cost: string;
  planned_contractor_cost: string;
  planned_contractor_cost_60d: string;
  planned_internal_cost_60d: string;
  planned_revenue_60d: string;
  one_time_internal_cost: string;
  recurring_internal_cost: string;
  salary_cost: string;
  overhead_narzut: string; // narzut wartościowy (>0 tylko w DIRECT): nieprojektowi + globalne stałe wg wartości
  overhead_narzut_people: string; // część osobowa narzutu (płace nieprojektowych wg wartości)
  overhead_narzut_fixed: string; // część kosztów stałych narzutu (globalne koszty stałe wg wartości)
  official_fee_cost: string;
  planned_official_fee_cost: string;
  total_actual_cost: string;
  balance: string;
  total_value: string | null;
  expected_cash_balance_60d: string;
  current_cash_balance: string;
  expected_inflows_60d: string;
  expected_outflows_60d: string;
  outstanding_pgk_60d: string;
  planned_contractor_cost_60d_remaining: string;
  approved_pock_60d: string;
  future_recurring_60d: string;
  future_salary_60d: string;
  planned_official_fee_cost_60d: string;
  paid_one_time_internal_cost: string;
  paid_recurring_to_today: string;
  paid_salary_to_today: string;
}

export type CashflowEventType =
  | "client_invoice"
  | "client_invoice_paid"
  | "approved_draft"
  | "planned_revenue"
  | "contractor_invoice"
  | "contractor_invoice_payment"
  | "planned_contractor_cost"
  | "pock_approved_cost"
  | "one_time_cost"
  | "recurring_cost"
  | "salary_cost"
  | "intercompany_cost"
  | "intercompany_revenue"
  | "tax_entry"
  | "official_fee_cost"
  | "planned_official_fee_cost"
  | "onetime_aggregate"
  | "recurring_aggregate"
  | "salary_aggregate";

export interface SalaryCostBreakdownItem {
  employee_name: string;
  fraction: number;
  monthly_cost: string;
  cost: string;
}

export interface CompanyBreakdownItem {
  company_code: string;
  amount: string;
}

export interface CashflowEvent {
  date: string;
  amount: string;
  type: CashflowEventType;
  label: string;
  url: string;
  is_actual: boolean;
  payment_status?: string;
  entity_id?: string | null;
  company_id?: string | null;
  breakdown?: SalaryCostBreakdownItem[];
  company_breakdown?: CompanyBreakdownItem[];
}

export type ConversationType = "director" | "board";

export interface ProjectMessageAttachment {
  id: string;
  name: string;
  content_type: string;
  file_url: string | null;
}

export interface ProjectMessage {
  id: string;
  project: string;
  conversation_type: ConversationType;
  author: string;
  author_name: string;
  author_email: string;
  author_profile_picture_url: string | null;
  content: string;
  parent: string | null;
  is_pinned: boolean;
  replies: ProjectMessage[];
  attachments: ProjectMessageAttachment[];
  created_at: string;
  updated_at: string;
}

export interface MentionUser {
  id: string;
  name: string;
  email: string;
}
